import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Radio } from 'antd';
import useChatFeature from '../hooks/useChatFeature';
import useSandbox from '../hooks/useSandbox';
import { getSettings, updateSettings } from '../services/SettingsService';
import { refreshPlanFeatures } from '../hooks/usePlanFeatures';
import ModelKeyForm from '../components/sandbox/ModelKeyForm';
import { modelChoices } from '../components/sandbox/modelChoices';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './ChatFeature.css';

/**
 * Features › Chat (sandbox-first experience, docs/SANDBOX_FIRST.md): turns Flow's own assistant on or off, with the
 * organization's models (AI Foundry / adk-be-app keys, `config.chatOrgModels`) or the person's own key (kept by
 * mia-flow-bff). The person's plan can leave either out (`plan.features.chatOrgModels` / `chatOwnKey`).
 */
export default function ChatFeature() {
  const navigate = useNavigate();
  const [enabled, setEnabled, available] = useChatFeature();
  const { sandbox } = useSandbox({ intervalMs: 60000 });
  const { org, own } = modelChoices(sandbox);
  const [choice, setChoice] = useState(null);
  const [ownKey, setOwnKey] = useState(undefined);
  const picked = choice || (org.available ? 'org' : 'own');

  // Someone who chose their own key before finds it picked.
  useEffect(() => {
    getSettings().then((value) => { if (value?.onboarding?.keySource === 'own') { setChoice((current) => current || 'own'); } }).catch(() => {});
  }, []);

  // Going back to the organization's models is a choice too: saved, so the chat uses AI Foundry again (choosing your
  // own key is saved by the key form, once a key is checked).
  const pick = (value) => {
    setChoice(value);
    if (value === 'org' && org.available) {
      updateSettings({ onboarding: { keySource: 'org' } }).then(() => refreshPlanFeatures()).catch(() => {});
    }
  };

  const canTurnOn = (picked === 'org' && org.available) || (picked === 'own' && own.available && Boolean(ownKey?.configured));
  // Turned on: back to Home, which now starts with the prompt, the cursor in it.
  const turnOn = () => {
    setEnabled(true);
    navigate('/', { state: { focusPrompt: true } });
  };

  // The person's plan has no chat (neither the organization's models nor an own key): nothing to turn on here.
  if (!available) {
    const planName = sandbox?.plan?.name;
    return (
      <div className="sbx-home">
        <div className="sbx-home-inner chat-feature">
          <div className="sbx-home-title">
            <span className="sbx-label">Feature</span>
            <div className="sbx-home-title-row"><h1>Chat</h1></div>
            <p data-testid="chat-not-in-plan">
              Flow’s assistant is not included in {planName ? `your ${planName} plan` : 'your plan'}. Your projects and your
              AI tools work as before; ask your admin for another plan to use it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner chat-feature">
        <div className="sbx-home-title">
          <span className="sbx-label">Feature</span>
          <div className="sbx-home-title-row">
            <h1>Chat</h1>
            <span className={`sbx-pill ${enabled ? 'sbx-pill-running' : ''}`}>{enabled ? 'On' : 'Off'}</span>
          </div>
          <p>
            Talk to an assistant next to your code, previews and logs, in every project. Your AI tools keep working as
            before; this only adds Flow’s own assistant.
          </p>
        </div>

        <Radio.Group className="chat-feature-options" onChange={(event) => pick(event.target.value)} value={picked}>
          <label className={`sbx-card chat-feature-option${picked === 'org' ? ' selected' : ''}${org.available ? '' : ' disabled'}`}>
            <Radio disabled={!org.available} value="org" />
            <span className="chat-feature-option-text">
              <strong>Use your organization’s models <span className="sbx-pill chat-feature-premium">Premium</span></strong>
              <span>Your admin provides the keys through AI Foundry. Nothing to set up.</span>
              <span className={org.available ? 'chat-feature-ok' : ''}>{org.note}</span>
            </span>
          </label>
          {/* Not a label: it holds the key form, whose inputs must not toggle the radio. */}
          <div className={`sbx-card chat-feature-option${picked === 'own' ? ' selected' : ''}${own.available ? '' : ' disabled'}`} onClick={() => own.available && setChoice('own')}>
            <Radio aria-label="Use my own key" disabled={!own.available} value="own" />
            <span className="chat-feature-option-text">
              <strong>Use my own key</strong>
              <span>Paste a key from your model provider. It is used only for your chats and never shown again.</span>
              {!own.available && <span>{own.note}</span>}
              {own.available && picked === 'own' && <ModelKeyForm idPrefix="chat-key" onChange={setOwnKey} />}
            </span>
          </div>
        </Radio.Group>

        <div className="chat-feature-actions">
          {enabled ? (
            <>
              <Button onClick={() => navigate('/chat')} type="primary">Open chat</Button>
              <Button onClick={() => setEnabled(false)}>Turn off chat</Button>
              <span>Your projects are not affected; the assistant panel goes away.</span>
            </>
          ) : (
            <>
              <Button disabled={!canTurnOn} onClick={turnOn} type="primary">Turn on chat</Button>
              <span>Adds the Flow Assistant to your projects and the prompt box to your home.</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
